import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ShieldAlert, RotateCw, Check, AlertTriangle, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import {
  adminListSecurityAlerts,
  adminAckSecurityAlert,
  adminTriggerSecuritySweep,
  adminRlsAudit,
} from "@/lib/admin-security.functions";

export const Route = createFileRoute("/_authenticated/admin/security")({
  head: () => ({
    meta: [
      { title: "Security — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminSecurityPage,
});

type Tab = "alerts" | "audit";

function severityVariant(s: string): "default" | "secondary" | "destructive" | "outline" {
  if (s === "critical" || s === "high") return "destructive";
  if (s === "warn") return "default";
  return "secondary";
}

function AdminSecurityPage() {
  const [tab, setTab] = useState<Tab>("alerts");
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center gap-3 mb-6">
          <ShieldAlert className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Security</h1>
            <p className="text-sm text-muted-foreground">
              Monitored alerts and a live audit of RLS policies, grants, and SECURITY DEFINER functions.
            </p>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setTab("alerts")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "alerts" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}
          >
            Alerts
          </button>
          <button
            onClick={() => setTab("audit")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "audit" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}
          >
            RLS / Grants audit
          </button>
        </div>

        {tab === "alerts" ? <AlertsTab /> : <AuditTab />}
      </main>
      <Footer />
    </div>
  );
}

function AlertsTab() {
  const list = useServerFn(adminListSecurityAlerts);
  const ack = useServerFn(adminAckSecurityAlert);
  const sweep = useServerFn(adminTriggerSecuritySweep);
  const q = useQuery({
    queryKey: ["admin-security-alerts"],
    queryFn: () => list({ data: undefined as never }),
    refetchOnWindowFocus: false,
  });

  const alerts = (q.data?.alerts ?? []) as Array<{
    id: string; detected_at: string; category: string; severity: string; title: string;
    detail: string | null; metric: number | null; threshold: number | null;
    acknowledged_at: string | null;
  }>;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {alerts.length} alert{alerts.length === 1 ? "" : "s"} (latest 200). Sweep runs every 15 minutes.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try { await sweep({ data: undefined as never }); toast.success("Sweep complete"); q.refetch(); }
              catch (e) { toast.error(String((e as Error).message)); }
            }}
          >
            <RotateCw className="h-4 w-4 mr-1" /> Run sweep now
          </Button>
        </div>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!q.isLoading && alerts.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center">
          <Check className="h-8 w-8 mx-auto text-green-600 mb-2" />
          <p className="font-medium">No security alerts detected.</p>
        </div>
      )}

      <ul className="space-y-3">
        {alerts.map((a) => (
          <li key={a.id} className="rounded-lg border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant={severityVariant(a.severity)}>{a.severity}</Badge>
                  <Badge variant="outline">{a.category}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(a.detected_at).toLocaleString()}</span>
                  {a.acknowledged_at && <Badge variant="secondary">acknowledged</Badge>}
                </div>
                <h3 className="font-semibold">{a.title}</h3>
                {a.detail && <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>}
                {a.metric !== null && (
                  <p className="text-xs text-muted-foreground mt-1">
                    metric={a.metric} threshold={a.threshold ?? "—"}
                  </p>
                )}
              </div>
              {!a.acknowledged_at && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try { await ack({ data: { id: a.id } }); toast.success("Acknowledged"); q.refetch(); }
                    catch (e) { toast.error(String((e as Error).message)); }
                  }}
                >
                  Acknowledge
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AuditTab() {
  const audit = useServerFn(adminRlsAudit);
  const q = useQuery({
    queryKey: ["admin-rls-audit"],
    queryFn: () => audit({ data: undefined as never }),
    refetchOnWindowFocus: false,
  });
  const [filter, setFilter] = useState("");

  const flagged = useMemo(() => {
    if (!q.data) return [] as Array<{ kind: string; text: string }>;
    const out: Array<{ kind: string; text: string }> = [];
    for (const t of q.data.tables) {
      if (!t.rls_enabled) out.push({ kind: "RLS disabled", text: `Table ${t.table_name} has RLS disabled` });
    }
    for (const p of q.data.policies) {
      const wide = (p.with_check === "true" || (p.qual === "true" && p.cmd !== "SELECT"));
      const anonWrite = (p.roles ?? []).includes("anon") && p.cmd !== "SELECT";
      if (wide) out.push({ kind: "Permissive policy", text: `${p.table_name}.${p.policy_name} (${p.cmd}) uses USING/WITH CHECK true` });
      if (anonWrite) out.push({ kind: "Anon write", text: `${p.table_name}.${p.policy_name} grants ${p.cmd} to anon` });
    }
    for (const g of q.data.table_grants) {
      if (g.grantee === "anon" && g.privilege !== "SELECT") {
        out.push({ kind: "Anon table privilege", text: `${g.table_name}: anon has ${g.privilege}` });
      }
    }
    return out;
  }, [q.data]);

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading audit…</p>;
  if (q.error) return <p className="text-sm text-destructive">Failed: {String((q.error as Error).message)}</p>;
  if (!q.data) return null;

  const matches = (s: string) => filter === "" || s.toLowerCase().includes(filter.toLowerCase());

  return (
    <section className="space-y-6">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <h2 className="font-semibold">Flagged access paths ({flagged.length})</h2>
        </div>
        {flagged.length === 0 ? (
          <p className="text-sm text-muted-foreground">No unintended public access paths detected.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {flagged.map((f, i) => (
              <li key={i} className="flex gap-2"><Badge variant="destructive">{f.kind}</Badge><span>{f.text}</span></li>
            ))}
          </ul>
        )}
      </div>

      <Input
        placeholder="Filter by table, policy, column, function…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="max-w-md"
      />

      <details open className="rounded-lg border bg-card p-4">
        <summary className="font-semibold cursor-pointer flex items-center gap-2">
          <Lock className="h-4 w-4" /> Tables — RLS status ({q.data.tables.length})
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Table</th><th>RLS</th><th>Forced</th></tr></thead>
            <tbody>
              {q.data.tables.filter((t) => matches(t.table_name)).map((t) => (
                <tr key={t.table_name} className="border-t">
                  <td className="py-1 pr-3 font-mono">{t.table_name}</td>
                  <td>{t.rls_enabled ? <Badge>on</Badge> : <Badge variant="destructive">off</Badge>}</td>
                  <td>{t.rls_forced ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="rounded-lg border bg-card p-4">
        <summary className="font-semibold cursor-pointer flex items-center gap-2">
          <Eye className="h-4 w-4" /> RLS policies ({q.data.policies.length})
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Table</th><th className="pr-3">Policy</th><th className="pr-3">Cmd</th><th className="pr-3">Roles</th><th className="pr-3">USING</th><th>WITH CHECK</th></tr></thead>
            <tbody>
              {q.data.policies.filter((p) => matches(p.table_name) || matches(p.policy_name)).map((p, i) => (
                <tr key={i} className="border-t align-top">
                  <td className="py-1 pr-3 font-mono">{p.table_name}</td>
                  <td className="pr-3">{p.policy_name}</td>
                  <td className="pr-3">{p.cmd}</td>
                  <td className="pr-3">{(p.roles ?? []).join(", ")}</td>
                  <td className="pr-3 font-mono break-all">{p.qual ?? ""}</td>
                  <td className="font-mono break-all">{p.with_check ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="rounded-lg border bg-card p-4">
        <summary className="font-semibold cursor-pointer">Table grants ({q.data.table_grants.length})</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Table</th><th className="pr-3">Grantee</th><th>Privilege</th></tr></thead>
            <tbody>
              {q.data.table_grants.filter((g) => matches(g.table_name) || matches(g.grantee)).map((g, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1 pr-3 font-mono">{g.table_name}</td>
                  <td className="pr-3">{g.grantee}</td>
                  <td>{g.privilege}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="rounded-lg border bg-card p-4">
        <summary className="font-semibold cursor-pointer">Column grants ({q.data.column_grants.length})</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Table</th><th className="pr-3">Column</th><th className="pr-3">Grantee</th><th>Privilege</th></tr></thead>
            <tbody>
              {q.data.column_grants.filter((g) => matches(g.table_name) || matches(g.column_name) || matches(g.grantee)).map((g, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1 pr-3 font-mono">{g.table_name}</td>
                  <td className="pr-3 font-mono">{g.column_name}</td>
                  <td className="pr-3">{g.grantee}</td>
                  <td>{g.privilege}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <details className="rounded-lg border bg-card p-4">
        <summary className="font-semibold cursor-pointer">SECURITY DEFINER functions ({q.data.function_grants.length})</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-3">Function</th><th className="pr-3">Grantee</th><th>Privilege</th></tr></thead>
            <tbody>
              {q.data.function_grants.filter((g) => matches(g.function) || matches(g.grantee ?? "")).map((g, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1 pr-3 font-mono">{g.function}</td>
                  <td className="pr-3">{g.grantee ?? "—"}</td>
                  <td>{g.privilege ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <p className="text-xs text-muted-foreground">Snapshot generated {new Date(q.data.generated_at).toLocaleString()}</p>
    </section>
  );
}
